import { Check, ChevronDown, LoaderCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { QuestionAnswerBlockStatus } from '@/entities/ask-syncly/ask-syncly-constants';
import { MotionDiv } from '@/shared/components/motion-div';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/shadcn/collapsible';
import { Stage } from '@/shared/components/stage/stage';
import { ToggleStage } from '@/shared/components/stage/toggle-stage';
import { cn } from '@/shared/utils/cn';
import { motion } from 'motion/react';

type Props = {
  reasons: string[];
  status: QuestionAnswerBlockStatus;
};

export function Reasoning(props: Props) {
  const { reasons, status } = props;
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (status === QuestionAnswerBlockStatus.COMPLETED) {
      setIsOpen(false);
    }
  }, [status]);

  const isThinking = useMemo(() => {
    if (status === QuestionAnswerBlockStatus.WAITING) return true;
    if (status === QuestionAnswerBlockStatus.REASONING) {
      return reasons.length === 0;
    }
    return false;
  }, [status, reasons]);

  return (
    <Stage className="flex flex-col gap-2">
      <Stage.Scene status={isThinking}>
        <div className="flex items-center gap-2">
          <AnimatedText text="Syncly is thinking ..." />
        </div>
      </Stage.Scene>
      <Stage.Scene default>
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger>
            <div className="flex items-center gap-2">
              <span className="font-family-mono text-muted-foreground text-xs font-medium">
                Reasoning
              </span>
              {status === QuestionAnswerBlockStatus.COMPLETED && (
                <ChevronDown
                  className={cn(
                    'text-muted-foreground size-3 transition-transform duration-200',
                    isOpen && '-rotate-180',
                  )}
                />
              )}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-3 flex flex-col gap-8">
            {reasons.map((reason, index, array) => (
              <ReasoningItem
                key={reason}
                reason={reason}
                loading={
                  index === array.length - 1 && status === QuestionAnswerBlockStatus.REASONING
                }
              />
            ))}
          </CollapsibleContent>
        </Collapsible>
        <ToggleStage status={status === QuestionAnswerBlockStatus.ANSWERING}>
          <AnimatedText text="Generating your insights ..." />
        </ToggleStage>
      </Stage.Scene>
    </Stage>
  );
}

export function ReasoningItem({ reason, loading }: { reason: string; loading: boolean }) {
  return (
    <MotionDiv className="flex items-start gap-2">
      <ToggleStage
        status={!loading}
        fallback={<LoaderCircle className="size-4 animate-spin text-blue-500" />}
      >
        <Check className="size-4 text-green-500" />
      </ToggleStage>
      <div
        className={cn(
          'font-family-mono line-clamp-1 flex flex-1 text-sm text-gray-500 transition-colors duration-300',
          loading && 'text-gray-800',
        )}
      >
        {reason}
      </div>
    </MotionDiv>
  );
}

export function AnimatedText({ text }: { text: string }) {
  const gradientStyle = `linear-gradient(90deg, var(--color-zinc-700) 0%, var(--color-zinc-600) 40%, var(--color-zinc-400) 45%, var(--color-zinc-400) 55%, var(--color-zinc-600) 60%, var(--color-zinc-700) 100%)`;

  return (
    <div className="flex items-center gap-1 px-2 py-2 font-medium">
      <motion.span
        className="font-family-mono text-muted-foreground text-xs font-medium"
        style={{
          background: gradientStyle,
          backgroundSize: '200% 100%',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          color: 'transparent',
        }}
        animate={{
          backgroundPosition: ['200% 0%', '-200% 0%'],
        }}
        transition={{
          duration: 1.8,
          repeat: Infinity,
          ease: 'easeOut',
        }}
      >
        {text}
      </motion.span>
    </div>
  );
}
